import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';
import { Button, Card, Container, IconButton } from '@mui/material';
import { Title } from 'shared/components';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import CardSettingsBeneficiaries from '../CardSettingsBeneficiares';
import CardSettingsDonations from '../CardSettingsDonations';
import CardSettingsReporting from '../CardSettingsReporting';
import CardSettingsDataQuality from '../CardSettingsDataQuality';
import CardSettingsImpacts from '../CardSettingsImpacts';
import useStyles from './styles';

const TYPE = {
  DONATIONS: 'Donations',
  BENEFICIARIES: 'Beneficiaries',
  REPORTING: 'Reporting Compliance',
  DATA_QUALITY: 'Data Quality',
  IMPACTS: 'Impacts'
};

const OPTIONS = {
  METRICS: ['# Reached', 'Amount Donated ($USD)', '# Programs Supported'],
  IMPACTS_METRIC: ['Total claim', 'SROI', 'Leverage'],
  SEGMENTS: [
    'None',
    'By Portfolio',
    'By Manager',
    'By Social Category',
    'By NPO'
  ],
  FILTER: ['All time', 'This Year'],
  FILTER_IMPACTS: ['All time', 'This Year'],
  COMPARE: [
    'No Comparison'
    // 'Benchmark Group'
  ],
  YEAR: [2020, 2019, 2018, 2017, 2016, 2015, 2014, 2013, 2012, 2011, 2010],
  COMPARISONYEAR: [2020, 2019, 2018, 2017, 2016, 2015, 2014, 2013, 2012, 2011, 2010],
  STATUS: ['Active', 'Archived', 'All'],
  DONATION_SEGMENTS: ['Portfolio', 'Service Collection', 'Manager'],
  REPORTING_SEGMENTS: ['Portfolio', 'Service Collection', 'Manager', 'NPO'],
  DATA_QUALITY_SEGMENTS: ['Portfolio', 'Service Collection', 'Manager', 'NPO'],
  IMPACTS_SEGMENTS: ['Portfolio', 'Manager', 'Year'],
  DONATIONS_COMPARE: ['No Comparison', 'Last Year'],
  REPORTING_COMPARE: ['No Comparison', 'My peers'],
  DATA_QUALITY_COMPARE: ['No Comparison', 'My peers'],
  DONATIONS_TYPE: ['All types', 'Cash', 'Goods', 'Volunteerism', 'Services'],
  INVITATION_AGO: [
    '15 Days',
    '30 Days',
    '2 months',
    '3 months',
    '6 months',
    '1 year',
    'No limit'
  ],
  CATALIZED_OPTIONS: ['Catalyzed Claim', 'Non Catalyzed'],
  ALLOCATION_AMOUNT: [
    'All',
    '$1000 or less',
    '$1000 - $10K',
    '$10K - $100K',
    '$100K - $1M',
    '$1M - $10M',
    '$10M - $100M',
    '$100M or more'
  ]
};

const UTIL = {
  SERIALIZE: {
    SEGMENT: {
      None: 'None',
      'By Portfolio': 'Portfolio',
      'By Manager': 'Manager',
      'By Social Category': 'Service Collection',
      'By NPO': 'NPO'
    },
    METRIC: {
      '# Reached': 'Beneficiaries Reached',
      'Amount Donated ($USD)': '$ Allocated',
      '# Programs Supported': 'Report Count'
    }
  },
  DESERIALIZE: {
    SEGMENT: {
      None: 'None',
      Portfolio: 'By Portfolio',
      Manager: 'By Manager',
      'Service Collection': 'By Social Category',
      NPO: 'By NPO'
    },
    METRIC: {
      'Beneficiaries Reached': '# Reached',
      '$ Allocated': 'Amount Donated ($USD)',
      'Report Count': '# Programs Supported'
    }
  }
};

const getSettings = (type, settings, portfolios, services, isNew, indicators, beneficiaries) => {
  switch (type) {
    case TYPE.BENEFICIARIES:
      return {
        metric: settings.metric || OPTIONS.METRICS[0],
        segment: settings.segment || OPTIONS.SEGMENTS[1],
        filter: settings.filter || 'All time',
        year: settings.year || [],
        beneficiaries: (settings.beneficiaries) || ((isNew) ? beneficiaries.slice(0, 6).map(b => b.id) : []),
        switch: settings.switch || 'Any',
        comparisonyear: settings.comparisonyear || 'No Comparison',
        compare: settings.compare || '',
        status: settings.status || 'Active',
        customName: settings.customName || ''
      };
    case TYPE.DONATIONS:
      return {
        segment: settings.segment || OPTIONS.DONATION_SEGMENTS[0],
        filter: settings.filter || 'All time',
        year: settings.year || [],
        donationType: settings.donationType || 'All types',
        switch: settings.switch || '',
        comparisonyear: settings.comparisonyear || 'None',
        compare: settings.compare || 'No Comparison',
        status: settings.status || 'Active',
        customName: settings.customName || ''
      };
    case TYPE.REPORTING:
      return {
        segment: settings.segment || OPTIONS.REPORTING_SEGMENTS[0],
        filter: settings.filter || 'All time',
        year: settings.year || [],
        invitationAgo: settings.invitationAgo || OPTIONS.INVITATION_AGO[6],
        comparisonyear: settings.comparisonyear || 'No Comparison',
        compare: settings.compare || '',
        status: settings.status || 'Active',
        customName: settings.customName || ''
      };
    case TYPE.DATA_QUALITY:
      return {
        segment: settings.segment || OPTIONS.DATA_QUALITY_SEGMENTS[3],
        filter: settings.filter || 'All time',
        year: settings.year || [],
        comparisonyear: settings.comparisonyear || 'No Comparison',
        compare: settings.compare || '',
        status: settings.status || 'Active',
        customName: settings.customName || ''
      };
    case TYPE.IMPACTS:
      return {
        metric: settings.metric || OPTIONS.IMPACTS_METRIC[0],
        segment: settings.segment || OPTIONS.IMPACTS_SEGMENTS[0],
        filter: settings.filter || 'All time',
        year: settings.year || [],
        indicators: (settings.indicators) || ((isNew) ? indicators.slice(0, 6) : []),
        catalized:
          typeof settings.catalized === 'boolean' ? settings.catalized : true,
        portfolios: settings.portfolios || [],
        services: settings.services || [],
        allocation: settings.allocation || OPTIONS.ALLOCATION_AMOUNT[0],
        status: settings.status || 'Active',
        customName: settings.customName || ''
      };
    default:
      return null;
  }
};

const CardSettings = ({
  onClose,
  card,
  onDelete,
  onSave,
  positiveButtonText = 'SAVE',
  negativebuttonText = 'DELETE',
  title = 'Settings',
  noCloseIcon,
  isNew = false
}) => {
  const classes = useStyles();
  const beneficiaries = useSelector(state => state.beneficiaries.list);
  const { settings = {}, title: cardType } = card;
  const indicators = useSelector(state => state.filters.indicators);
  const portfolios = useSelector(state => state.filters.portfolios).map(
    data => data.name
  );
  const services = useSelector(state => state.filters.services);

  const deserializeSettings = rawSettings => {
    if (cardType === TYPE.BENEFICIARIES) {
      const deserializedSettings = { ...rawSettings };
      const segment = UTIL.DESERIALIZE.SEGMENT[rawSettings.segment];
      const metric = UTIL.DESERIALIZE.METRIC[rawSettings.metric];
      if (segment) deserializedSettings.segment = segment;
      if (metric) deserializedSettings.metric = metric;
      return deserializedSettings;
    }
    return rawSettings;
  };

  const defaultSettings = getSettings(cardType, deserializeSettings(settings), portfolios, services, isNew, indicators, beneficiaries);

  const [form, setForm] = React.useState(defaultSettings);

  const handleDelete = () => {
    onDelete(card.id);
  };

  const serializeForm = rawForm => {
    const serializedForm = { ...rawForm };
    serializedForm.segment = UTIL.SERIALIZE.SEGMENT[rawForm.segment];
    serializedForm.metric = UTIL.SERIALIZE.METRIC[rawForm.metric];
    return serializedForm;
  };

  const handleSave = () => {
    const serializedForm =
      cardType === TYPE.BENEFICIARIES ? serializeForm(form) : form;
    if (typeof onSave === 'function') {
      onSave(card, serializedForm);
    }
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const handleChangeForm = (field, value) => {
    const updateForm = cloneDeep(form);
    if (field === 'compare' && form.compare === OPTIONS.COMPARE[0]) {
      value = '';
    }
    if (field === 'compare' && form.compare === '') {
      updateForm.comparisonyear = 'None';
    }
    if (field === 'year' && form.filter.lenght !== '') {
      updateForm.filter = '';
    }
    if (field === 'comparisonyear' && form.compare !== '') {
      updateForm.compare = '';
    }
    if (field === 'filter' && form.year.lenght !== 0) {
      updateForm.year = [];
    }
    updateForm[field] = value;
    setForm(updateForm);
  };

  const getSegmentsOptions = type => {
    switch (type) {
      case TYPE.BENEFICIARIES:
        return OPTIONS.SEGMENTS;
      case TYPE.DONATIONS:
        return OPTIONS.DONATION_SEGMENTS;
      case TYPE.REPORTING:
        return OPTIONS.REPORTING_SEGMENTS;
      case TYPE.DATA_QUALITY:
        return OPTIONS.DATA_QUALITY_SEGMENTS;
      case TYPE.IMPACTS:
        return OPTIONS.IMPACTS_SEGMENTS;
      default:
        return OPTIONS.SEGMENTS;
    }
  };

  const getCompareOptions = type => {
    switch (type) {
      case TYPE.BENEFICIARIES:
        return OPTIONS.COMPARE;
      case TYPE.DONATIONS:
        return OPTIONS.DONATIONS_COMPARE;
      case TYPE.REPORTING:
        return OPTIONS.REPORTING_COMPARE;
      case TYPE.DATA_QUALITY:
        return OPTIONS.DATA_QUALITY_COMPARE;
      default:
        return OPTIONS.COMPARE;
    }
  };

  const isBeneficiaries = cardType === TYPE.BENEFICIARIES;
  const isDonations = cardType === TYPE.DONATIONS;
  const isReporting = cardType === TYPE.REPORTING;
  const isDataQuality = cardType === TYPE.DATA_QUALITY;
  const isImpacts = cardType === TYPE.IMPACTS;
  const isValid =
    isBeneficiaries || isDonations || isReporting || isDataQuality || isImpacts;

  return (
    <Card className={classes.card}>
      {!noCloseIcon && (
        <IconButton
          onClick={onClose}
          className={classes.icon}
          color="primary"
          component="span"
        >
          <CloseIcon />
        </IconButton>
      )}
      {isValid && (
        <>
          <Title className={classes.cardTitle}>{title}</Title>
        </>
      )}
      {isBeneficiaries && (
        <CardSettingsBeneficiaries
          OPTIONS={OPTIONS}
          form={form}
          beneficiaries={[{name: 'All beneficiary types', id: 0}, ...beneficiaries]}
          getSegmentsOptions={getSegmentsOptions}
          getCompareOptions={getCompareOptions}
          handleChangeForm={handleChangeForm}
        />
      )}
      {isDonations && (
        <CardSettingsDonations
          OPTIONS={OPTIONS}
          segmentOptions={getSegmentsOptions(cardType)}
          handleChangeForm={handleChangeForm}
          compareOptions={getCompareOptions(cardType)}
          form={form}
        />
      )}
      {isReporting && (
        <CardSettingsReporting
          OPTIONS={OPTIONS}
          segmentOptions={getSegmentsOptions(cardType)}
          handleChangeForm={handleChangeForm}
          compareOptions={getCompareOptions(cardType)}
          form={form}
        />
      )}
      {isDataQuality && (
        <CardSettingsDataQuality
          OPTIONS={OPTIONS}
          segmentOptions={getSegmentsOptions(cardType)}
          handleChangeForm={handleChangeForm}
          compareOptions={getCompareOptions(cardType)}
          form={form}
        />
      )}
      {isImpacts && (
        <CardSettingsImpacts
          indicators={indicators}
          portfolios={portfolios}
          services={services}
          options={OPTIONS}
          handleChangeForm={handleChangeForm}
          form={form}
        />
      )}
      {isValid && (
        <Container className={classes.actions}>
          <div className={classes.root}>
            {!isNew && (
              <Button
                variant="outlined"
                color="primary"
                className={classes.button}
                endIcon={<DeleteIcon />}
                onClick={handleDelete}
              >
                {negativebuttonText}
              </Button>
            )}
            <Button
            variant="outlined"
              color="primary"
              className={classes.button}
              endIcon={<CloseIcon />}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              className={classes.button}
              endIcon={<SaveIcon />}
              onClick={handleSave}
            >
              {positiveButtonText}
            </Button>
          </div>
        </Container>
      )}
      {!isValid && (
        <div className={classes.root}>
          <Title>
            The settings for this type of card is under construction.
          </Title>
        </div>
      )}
    </Card>
  );
};

CardSettings.propTypes = {
  onClose: PropTypes.func,
  card: PropTypes.object,
  onDelete: PropTypes.func,
  onSave: PropTypes.func,
  positiveButtonText: PropTypes.string,
  negativebuttonText: PropTypes.string,
  title: PropTypes.string,
  noCloseIcon: PropTypes.bool,
  isNew: PropTypes.bool,
};

export default CardSettings;
